/* START_MODULE_CONTRACT
 * PURPOSE: Frontend UI module: ProjectList.
 * SCOPE: React component or page.
 * DEPENDS: M-FE-API, M-FE-AUTH
 * LINKS: M-FE-PROJECTS
 * END_MODULE_CONTRACT
 *
 * START_MODULE_MAP
 * ProjectList - Отображение сетки проектов, поиск и пустые состояния.
 * END_MODULE_MAP
 *
 * START_CHANGE_SUMMARY
 * LAST_CHANGE: 2026-07-14 - UX: сетка карточек, поиск по имени, направляющее пустое состояние с CTA.
 * LAST_CHANGE: 2026-07-02 - GRACE coverage: контракт компонента и парный END_METHOD.
 * END_CHANGE_SUMMARY
 */

import { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Button from '@mui/material/Button';
import SearchIcon from '@mui/icons-material/Search';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import AddIcon from '@mui/icons-material/Add';
import ProjectItem from './ProjectItem';

/* START_METHOD_ProjectList */
/* START_CONTRACT: ProjectList
 * PURPOSE: Отобразить сетку проектов с поиском или направляющие пустые состояния.
 * INPUTS: { projects: Project[], onEdit: (p)=>void, onDelete: (p)=>void, onCreate?: ()=>void }.
 * OUTPUTS: { JSX.Element - список }.
 * SIDE_EFFECTS: локальный state поиска.
 * LINKS: M-FE-PROJECTS
 * END_CONTRACT: ProjectList
 */
export default function ProjectList({ projects, onEdit, onDelete, onCreate }) {
  const [searchQuery, setSearchQuery] = useState('');

  const normalizedQuery = searchQuery.trim().toLowerCase();

  const filteredProjects = useMemo(() => {
    if (!normalizedQuery) {
      return projects;
    }
    return projects.filter((project) =>
      project.name.toLowerCase().includes(normalizedQuery),
    );
  }, [projects, normalizedQuery]);

  if (projects.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1.5,
          py: { xs: 6, sm: 8 },
          px: 3,
          textAlign: 'center',
          borderRadius: 1,
          border: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          backgroundImage: (theme) =>
            `radial-gradient(ellipse 80% 60% at 50% 0%, ${theme.palette.primary.main}08 0%, transparent 70%)`,
        }}
      >
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: (theme) => theme.palette.primary.main + '12',
            color: 'primary.main',
            mb: 0.5,
          }}
        >
          <FolderOpenOutlinedIcon sx={{ fontSize: 32 }} />
        </Box>
        <Typography variant="subtitle1" fontWeight={600}>
          Пока нет проектов
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 360 }}>
          Создайте первый проект, чтобы назначать роли, сотрудников и задачи команды.
        </Typography>
        {onCreate && (
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={onCreate}
            sx={{ mt: 1 }}
          >
            Создать проект
          </Button>
        )}
      </Box>
    );
  }

  return (
    <Box>
      <TextField
        value={searchQuery}
        onChange={(event) => setSearchQuery(event.target.value)}
        placeholder="Найти проект…"
        size="small"
        fullWidth
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
            'aria-label': 'Поиск проекта',
          },
        }}
        sx={{
          mb: 2,
          maxWidth: { sm: 360 },
          '& .MuiOutlinedInput-root': {
            bgcolor: 'background.paper',
          },
        }}
      />

      {filteredProjects.length === 0 ? (
        <Box
          sx={{
            py: 6,
            px: 3,
            textAlign: 'center',
            borderRadius: 1,
            border: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            По запросу «{searchQuery.trim()}» ничего не найдено
          </Typography>
          <Button size="small" onClick={() => setSearchQuery('')}>
            Сбросить поиск
          </Button>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(auto-fill, minmax(280px, 1fr))',
            },
            gap: 2,
          }}
        >
          {filteredProjects.map((project) => (
            <ProjectItem
              key={project.id}
              project={project}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}
/* END_METHOD_ProjectList */
